import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { BreadCrumb } from "primereact/breadcrumb";
import { TabView, TabPanel } from "primereact/tabview";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import SvgDot from "../../../../assets/icons/SvgDot";
import SvgSearchIcon from "../../../../assets/icons/SvgSearchIcon";
import { Card } from "primereact/card";
import "./index.scss";

const AutomatedRemittanceMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useParams();
  const { data } = location.state || {};

  const [activeIndex, setActiveIndex] = useState(0);
  const [formData, setFormData] = useState({
    ruleCode: "",
    ruleName: "",
    isActive: false,
    frequency: null,
    processingDay: 1,
    cutoffDays: 3,
    minTransCount: 1,
    mainAccount: "",
    subAccount: "",
    branchDept: null,
  });

  const frequencyOptions = [
    { label: "Daily", value: "Daily" },
    { label: "Weekly", value: "Weekly" },
    { label: "Monthly", value: "Monthly" },
    { label: "Quarterly", value: "Quarterly" },
  ];

  const branchOptions = [
    { label: "Head Office", value: "HO" },
    { label: "Branch 1", value: "B1" },
    { label: "Branch 2", value: "B2" },
    { label: "Finance Department", value: "FIN" },
    { label: "Operations Department", value: "OPS" },
  ];

  const items = [
    { label: "Remittance Master", url: "/master/finance/remittance" },
    { label: "Automated Remittance Setup", url: "#" },
  ];

  const home = { label: "Master" };

  useEffect(() => {
    if (mode === "edit" || mode === "view") {
      // Load existing data
      if (data) {
        setFormData({
          ruleCode: data.code || "ARM-0001",
          ruleName: data.name || "Monthly Auto Remittance",
          isActive: data.status || false,
          frequency: "Monthly",
          processingDay: 25,
          cutoffDays: 3,
          minTransCount: 1,
          mainAccount: "ACC-1001",
          subAccount: "SUB-2001",
          branchDept: "HO",
        });
      }
    } else {
      // Generate new code for add mode
      setFormData(prev => ({
        ...prev,
        ruleCode: generateCode()
      }));
    }
  }, [mode, data]);

  const generateCode = () => {
    const random = Math.floor(Math.random() * 10000);
    return `ARM-${String(random).padStart(4, '0')}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    console.log("Saving remittance rule:", formData);
    // Add save logic here
    navigate("/master/finance/remittance");
  };

  const handleDelete = () => {
    if (window.confirm("Are you sure you want to delete this remittance rule?")) {
      console.log("Deleting remittance rule:", formData.ruleCode);
      // Add delete logic here
      navigate("/master/finance/remittance");
    }
  };

  const handleClose = () => {
    navigate("/master/finance/remittance");
  };

  const openAccountLookup = (accountType) => {
    console.log(`Opening ${accountType} lookup`);
    // Add lookup modal logic here
  };

  const isViewMode = mode === "view";

  return (
    <div className="container__automated__remittance__master">
        <div className="grid m-0 top__container">
          <div className="col-12 p-0">
            <Button
              icon="pi pi-arrow-left"
              className="back__button"
              onClick={handleClose}
            />
            <span className="page__title">Automated Remittance Master</span>
            <span className="mode-badge">{mode?.toUpperCase() || "ADD"}</span>
          </div>
          <div className="col-12 p-0">
            <BreadCrumb
              home={home}
              className="breadCrums__view__reversal"
              model={items}
              separatorIcon={<SvgDot color={"#000"} />}
            />
          </div>
        </div>

        <div className="content__container">
          <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
            <TabPanel header="General">
              <div className="tab-content">
                <div className="section-title">Automation Rule Configuration</div>
                <div className="form-grid three-column">
                  <div className="form-field">
                    <label htmlFor="ruleCode" className="required">Rule Code</label>
                    <InputText
                      id="ruleCode"
                      value={formData.ruleCode}
                      onChange={(e) => handleInputChange("ruleCode", e.target.value)}
                      disabled={isViewMode || mode === "edit"}
                      placeholder="ARM-{0000}"
                      className="full-width"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="ruleName" className="required">Rule Name</label>
                    <InputText
                      id="ruleName"
                      value={formData.ruleName}
                      onChange={(e) => handleInputChange("ruleName", e.target.value)}
                      disabled={isViewMode}
                      placeholder="Enter rule name"
                      className="full-width"
                      maxLength={100}
                    />
                  </div>

                  <div className="form-field checkbox-field">
                    <div className="checkbox-wrapper">
                      <Checkbox
                        inputId="isActive"
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange("isActive", e.checked)}
                        disabled={isViewMode}
                      />
                      <label htmlFor="isActive" className="checkbox-label">Active</label>
                    </div>
                  </div>
                </div>

                <div className="section-title">Processing Rules</div>
                <div className="form-grid two-column">
                  <div className="form-field">
                    <label htmlFor="frequency" className="required">Frequency</label>
                    <Dropdown
                      id="frequency"
                      value={formData.frequency}
                      options={frequencyOptions}
                      onChange={(e) => handleInputChange("frequency", e.value)}
                      disabled={isViewMode}
                      placeholder="Select frequency"
                      className="full-width"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="processingDay">
                      Processing Day
                      {formData.frequency === "Monthly" && <span className="required">*</span>}
                    </label>
                    <InputNumber
                      id="processingDay"
                      value={formData.processingDay}
                      onValueChange={(e) => handleInputChange("processingDay", e.value)}
                      disabled={isViewMode || formData.frequency !== "Monthly"}
                      min={1}
                      max={31}
                      className="full-width"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="cutoffDays">
                      Cut-off Days
                      <span className="help-text">Days before period end</span>
                    </label>
                    <InputNumber
                      id="cutoffDays"
                      value={formData.cutoffDays}
                      onValueChange={(e) => handleInputChange("cutoffDays", e.value)}
                      disabled={isViewMode}
                      min={0}
                      max={30}
                      className="full-width"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="minTransCount">Min Transaction Count</label>
                    <InputNumber
                      id="minTransCount"
                      value={formData.minTransCount}
                      onValueChange={(e) => handleInputChange("minTransCount", e.value)}
                      disabled={isViewMode}
                      min={1}
                      className="full-width"
                    />
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel header="GL Mapping">
              <div className="tab-content">
                <div className="section-title">Account Configuration</div>
                <div className="form-grid three-column">
                  <div className="form-field">
                    <label htmlFor="mainAccount" className="required">Main Account</label>
                    <div className="input-with-button">
                      <InputText
                        id="mainAccount"
                        value={formData.mainAccount}
                        onChange={(e) => handleInputChange("mainAccount", e.target.value)}
                        disabled={isViewMode}
                        placeholder="Select main account"
                        className="full-width"
                      />
                      {!isViewMode && (
                        <Button
                          icon={<SvgSearchIcon />}
                          className="lookup-button"
                          onClick={() => openAccountLookup("main")}
                        />
                      )}
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="subAccount">Sub Account</label>
                    <div className="input-with-button">
                      <InputText
                        id="subAccount"
                        value={formData.subAccount}
                        onChange={(e) => handleInputChange("subAccount", e.target.value)}
                        disabled={isViewMode}
                        placeholder="Select sub account"
                        className="full-width"
                      />
                      {!isViewMode && (
                        <Button
                          icon={<SvgSearchIcon />}
                          className="lookup-button"
                          onClick={() => openAccountLookup("sub")}
                        />
                      )}
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="branchDept" className="required">Branch/Department</label>
                    <Dropdown
                      id="branchDept"
                      value={formData.branchDept}
                      options={branchOptions}
                      onChange={(e) => handleInputChange("branchDept", e.value)}
                      disabled={isViewMode}
                      placeholder="Select branch/department"
                      className="full-width"
                    />
                  </div>
                </div>
              </div>
            </TabPanel>
              </TabView>

              <div className="action-buttons">
            {!isViewMode && (
              <Button
                label="Save"
                className="save-button"
                onClick={handleSave}
              />
            )}
            {mode === "edit" && (
              <Button
                label="Delete"
                className="delete-button"
                onClick={handleDelete}
              />
            )}
            <Button
              label="Close"
              className="close-button"
              onClick={handleClose}
            />
              </div>
        </div>
      </div>
  );
};

export default AutomatedRemittanceMaster;